import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddPlus2, StartTimeLine, TableNoData } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { NUMBER } from '../../constant/Number.constant';
import { useAuth, USER_ROLE } from '../../hooks';
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions';
import {
  useFetchSupportNotesQuery,
  useLazyFetchSupportNotesQuery,
} from '../../services/supportnote.api';
import {
  FetchSupportNotesParams,
  SupportNote,
  SupportNoteSource,
} from '../../stores/types/supportnote.interface';
import { getLastMonth } from '../../utils/Date.Util';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Icon,
  Label,
  LabelType,
  MODAL_TYPES,
  Spinner,
  useGlobalModalContext,
} from '../_ui';
import { DATE_RANGE } from '../_ui/date-picker/Custom-Date-Picker.component';
import { GridPager } from '../_ui/grid/Grid-Pager.component';
import AddNoteEditor from './AddNoteEditor.component';
import { ChargerHistoryProps } from './ChargerHistory.interface';
import ChargerHistoryFilters from './ChargerHistoryFilters.component';
import ChargerHistoryTimelineItem from './ChargerHistoryTimelineItem.component';
import { ALL_EVENT_TYPES, NOTE_TYPES } from './ChargerHistoryUtils';

const ITEMS_PER_PAGE = NUMBER.TWENTY;

const ChargerHistory: FC<ChargerHistoryProps> = ({ chargerId, companyId, ...props }) => {
  const { t } = useTranslation();
  const { showModal } = useGlobalModalContext();
  const { role } = useAuth();
  const {
    canViewSupportNotes: hasViewSupportNotesPermissionOnCompany,
    canEditSupportNotes: hasEditSupportNotesPermissionOnCompany,
    isPermissionsLoading,
  } = useCompanyPermissions(companyId);
  const isSupportUser = role === USER_ROLE.SUPPORT;
  const canViewSupportNotes = hasViewSupportNotesPermissionOnCompany || isSupportUser;
  const canEditSupportNotes = hasEditSupportNotesPermissionOnCompany || isSupportUser;

  const [internalDateOption, setInternalDateOption] = useState<DATE_RANGE>(DATE_RANGE.LAST_MONTH);
  const [internalSelectedDate, setInternalSelectedDate] = useState<Date[]>(getLastMonth());
  const [internalSelectedTags, setInternalSelectedTags] = useState<string[]>(ALL_EVENT_TYPES);
  const [internalCurrentPage, setInternalCurrentPage] = useState(NUMBER.ONE);

  const dateOption = props.dateOption ?? internalDateOption;
  const setDateOption = props.setDateOption ?? setInternalDateOption;

  const selectedDate = props.selectedDate ?? internalSelectedDate;
  const setSelectedDate = props.setSelectedDate ?? setInternalSelectedDate;

  const selectedTags = props.selectedTags ?? internalSelectedTags;
  const setSelectedTags = props.setSelectedTags ?? setInternalSelectedTags;

  const currentPage = props.currentPage ?? internalCurrentPage;
  const setCurrentPage = props.setCurrentPage ?? setInternalCurrentPage;

  // Push the date range to the API (filter_ge/filter_le[createdMs]) instead of fetching
  // everything and filtering client-side -- the endpoint requires limit/offset and already
  // supports date filtering server-side; asking it to do the filtering avoids over-fetching a
  // charger's entire note history on every render and re-fetches correctly when the range changes.
  const queryParams: FetchSupportNotesParams = useMemo(
    () => ({
      'filter_eq[deviceId]': chargerId,
      limit: 1000,
      offset: 0,
      ...(selectedDate[0] && { 'filter_ge[createdMs]': selectedDate[0].toISOString() }),
      ...(selectedDate[1] && { 'filter_le[createdMs]': selectedDate[1].toISOString() }),
    }),
    [chargerId, selectedDate],
  );

  const { data, isLoading } = useFetchSupportNotesQuery(queryParams, {
    skip: !canViewSupportNotes,
  });

  const [fetchPage] = useLazyFetchSupportNotesQuery();

  const [allItems, setAllItems] = useState<SupportNote[]>([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  // Set from the LAST page fetched below. The backend computes this ignoring the date range's
  // lower bound, so it reflects whether we've reached this charger's true earliest matching
  // record -- not just the earliest one inside the currently selected date window.
  const [isEndOfHistory, setIsEndOfHistory] = useState(false);

  useEffect(() => {
    if (!data || !canViewSupportNotes) {
      return;
    }

    let cancelled = false;
    const pageLimit = data.limit || 1000;
    const totalCount = data.totalCount;

    const loadAll = async () => {
      setIsLoadingAll(true);
      const combined: SupportNote[] = [...data.items];
      let lastPageIsEndOfHistory = data.isEndOfHistory;

      try {
        let offset = data.offset + data.items.length;

        while (offset < totalCount) {
          if (cancelled) break;

          const page = await fetchPage({
            ...queryParams,
            offset,
            limit: pageLimit,
          }).unwrap();

          combined.push(...page.items);
          offset += page.items.length;
          lastPageIsEndOfHistory = page.isEndOfHistory;

          if (page.items.length === 0) break;
        }

        if (!cancelled) {
          setAllItems(combined);
          setIsEndOfHistory(lastPageIsEndOfHistory);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAll(false);
        }
      }
    };

    loadAll();

    return () => {
      cancelled = true;
    };
  }, [data, canViewSupportNotes, fetchPage, queryParams]);

  const isHistoryLoading = isPermissionsLoading || isLoading || isLoadingAll;

  const handleAddNote = useCallback(() => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('charger_history_add_new_note'),
      width: '540px',
      onRenderBody: () => <AddNoteEditor chargerId={chargerId} />,
      shouldCloseOnOverlayClick: false,
    });
  }, [showModal, t, chargerId]);

  const handleDateChange = (option: string, date: Date[]) => {
    setDateOption(option as DATE_RANGE);
    setSelectedDate(date);
    setCurrentPage(NUMBER.ONE);
  };

  const handleTagsChange = (tags: string[]) => {
    setSelectedTags(tags);
    setCurrentPage(NUMBER.ONE);
  };

  const items = allItems;

  const filteredItems = useMemo(() => {
    if (!items.length) return [];

    // Date range is now applied server-side (filter_ge/filter_le[createdMs] in queryParams above)
    // -- `items` is already restricted to the selected range, so only the tag/source checkboxes
    // need filtering here.
    const filtered = items.filter((item) => {
      // Source: MAINTENANCE
      if (item.source === SupportNoteSource.MAINTENANCE) {
        return selectedTags.includes(SupportNoteSource.MAINTENANCE);
      }

      // Source: JIRA / ZENDESK (synthetic ticket rows, always tag CHARGER)
      if (item.source === SupportNoteSource.JIRA || item.source === SupportNoteSource.ZENDESK) {
        return selectedTags.includes(item.source);
      }

      // Source: SUPPORT
      if (item.source === SupportNoteSource.SUPPORT) {
        // If item has a specific supported tag
        if (NOTE_TYPES.includes(item.tag)) {
          return selectedTags.includes(item.tag);
        }

        // Otherwise, match any of the supported tags
        return NOTE_TYPES.some((tag: string) => selectedTags.includes(tag));
      }

      return true;
    });

    // Sort by createdDateTime descending
    return filtered.sort(
      (a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime(),
    );
  }, [items, selectedTags]);

  const totalPage = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const hasMore = currentPage < totalPage;

  // Note: no "Add note" button here -- the toolbar above (filters row) already renders one
  // whenever `canEditSupportNotes`, and it stays visible even when `items` is empty, so
  // duplicating it here used to render two "Add note" buttons at once.
  const renderEmptyState = () => (
    <div className='flex flex-col justify-center items-center gap-4 py-8'>
      <div className='py-4 flex-col justify-start items-center gap-1 inline-flex'>
        <Icon src={TableNoData} size={IconSize.SIZE_48x48} />
        <div className='flex-col justify-start items-center gap-1 flex'>
          <Label
            text={t('charger_history_no_events')}
            type={LabelType.H4}
            color={ColorType.GREY5}
          />
          <Label
            text={t('charger_history_no_events_desc')}
            type={LabelType.BODY2}
            color={ColorType.GREY5}
          />
        </div>
      </div>
    </div>
  );

  // Only show the start-of-timeline marker on the last (client-side) page, and only when the
  // backend confirms there's truly nothing older for this charger -- not merely nothing older
  // inside the currently selected date window. See isEndOfHistory on SupportNoteListResponse.
  const shouldShowStartMarker = !hasMore && paginatedItems.length > 0 && isEndOfHistory;

  const renderTimeline = () => (
    <div className='flex flex-col'>
      {paginatedItems.map((item, index) => (
        <ChargerHistoryTimelineItem key={item.id} item={item} isFirst={index === 0} />
      ))}
      {/* Start of timeline marker */}
      {shouldShowStartMarker && (
        <div className='flex items-center pl-4'>
          <div className='flex items-center justify-center'>
            <Icon src={StartTimeLine} size={IconSize.SIZE_16x16} />
          </div>
        </div>
      )}
    </div>
  );

  const renderPagination = () => {
    if (totalPage <= 1) return null;

    return (
      <GridPager
        totalPage={totalPage}
        currentPage={currentPage}
        loadPage={setCurrentPage}
        showPageSearch={false}
      />
    );
  };

  return (
    <div className='flex flex-col pt-4'>
      {/* Filters row + Add note button. Shown even when `items` is empty -- the date range is
          now applied server-side, so an empty result is often just "nothing in this window" and
          the date picker here is the only way to widen it. */}
      {!isHistoryLoading && (
        <div className='flex flex-row items-center justify-between pb-4 flex-wrap gap-2'>
          <ChargerHistoryFilters
            dateOption={dateOption}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            selectedTags={selectedTags}
            onTagsChange={handleTagsChange}
          />
          {canEditSupportNotes && (
            <Button
              icon={AddPlus2}
              size={ButtonSize.SMALL}
              label={t('charger_history_add_note')}
              type={ButtonType.TERTIARY}
              onClick={handleAddNote}
            />
          )}
        </div>
      )}

      {/* Content */}
      <div className='overflow-y-auto pr-1'>
        {isHistoryLoading && (
          <div className='flex justify-center py-8'>
            <Spinner />
          </div>
        )}
        {!isHistoryLoading && items.length === 0 && renderEmptyState()}
        {!isHistoryLoading && items.length > 0 && paginatedItems.length === 0 && (
          <div className='flex justify-center py-8'>
            <Label
              text={
                selectedTags.length === 0
                  ? t('charger_history_select_filters')
                  : t('charger_history_no_matching_events')
              }
              type={LabelType.BODY2}
              color={ColorType.GREY5}
            />
          </div>
        )}
        {!isHistoryLoading && items.length > 0 && paginatedItems.length > 0 && (
          <>
            {renderTimeline()}
            {renderPagination()}
          </>
        )}
      </div>
    </div>
  );
};

export default ChargerHistory;
